/** A place plus what is waiting there — the shape the list page reads. */
export interface PlaceWithWaiting extends Place {
  waiting_count?: number
  waiting?: { id: number | string; title: string }[]
}

export interface Place {
  id: number
  user_id: number
  title: string
  address?: string | null
  latitude: number
  longitude: number
  radius_m: number
  google_place_id?: string | null
  tags?: string[]
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export interface PlaceInput {
  title: string
  address?: string | null
  latitude: number
  longitude: number
  radius_m?: number
  google_place_id?: string | null
  tags?: string[]
}

/** A suggestion at or above this confidence attaches without being asked —
 * one number shared by the edit form's picker and chat-side materialization. */
export const PLACE_AUTO_ATTACH_THRESHOLD = 0.6

export interface PlaceSuggestion {
  place_id: number
  title: string
  address?: string | null
  latitude: number
  longitude: number
  radius_m: number
  tags?: string[]
  confidence: number
  matched_terms?: string[]
}

export type PlaceEventType =
  | 'enter'
  | 'dismissed'
  | 'opened'
  | 'snoozed'
  | 'completed'

export interface PlaceEventPayload {
  place_id: number
  task_id: number
  task_type?: 'task' | 'action_item'
  event: PlaceEventType
  meta?: Record<string, unknown>
}

class PlaceService {
  private baseUrl = '/api/places'

  async list(): Promise<Place[]> {
    const resp = await fetch(this.baseUrl, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!resp.ok) throw new Error('Failed to load places')
    const data = await resp.json()
    return (data?.places ?? []) as Place[]
  }

  async create(input: PlaceInput): Promise<Place> {
    const resp = await fetch(this.baseUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok || !data.success) {
      throw new Error(data?.error || 'Failed to create place')
    }
    return data.place as Place
  }

  async update(id: number, updates: Partial<PlaceInput>): Promise<Place> {
    const resp = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok || !data.success) {
      throw new Error(data?.error || 'Failed to update place')
    }
    return data.place as Place
  }

  async remove(id: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok || !data.success) {
      throw new Error(data?.error || 'Failed to delete place')
    }
  }

  async suggestForTask(
    text: string,
    limit = 3,
  ): Promise<PlaceSuggestion[]> {
    if (!text || !text.trim()) return []
    const resp = await fetch(`${this.baseUrl}/suggest`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, limit }),
    })
    if (!resp.ok) return []
    const data = await resp.json()
    return (data?.candidates ?? []) as PlaceSuggestion[]
  }

  async recordEvent(payload: PlaceEventPayload): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/events`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    } catch {
      // Telemetry only — swallow.
    }
  }

  /**
   * Parse a Google Maps link of the form
   *   https://www.google.com/maps/@37.7749,-122.4194,15z
   * or /place/... or ?q=lat,lng. Also recognises the Saved-Places CSV shape
   *   https://www.google.com/maps/place/?q=place_id:ChIJ...
   * which has no coordinates but a stable place_id we want to preserve.
   * Returns null only if we got nothing at all.
   */
  parseMapsLink(url: string):
    | { lat: number; lng: number; place_id?: string | null }
    | { lat: null; lng: null; place_id: string }
    | null {
    if (!url) return null
    const placeIdMatch = url.match(/place_id[:=]([A-Za-z0-9_-]+)/)
    const placeId = placeIdMatch ? placeIdMatch[1] : null
    // Order matters: !3d/!4d is the PLACE PIN itself; @lat,lng is only the
    // viewport centre (close at high zoom, off by blocks when zoomed out).
    const patterns = [
      /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    ]
    for (const re of patterns) {
      const m = url.match(re)
      if (m) {
        const lat = parseFloat(m[1])
        const lng = parseFloat(m[2])
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng, place_id: placeId }
        }
      }
    }
    if (placeId) return { lat: null, lng: null, place_id: placeId }
    return null
  }

  /**
   * The place NAME carried by a Google Maps place URL —
   * /maps/place/Trader+Joe's/@37.6,-122.4,17z → "Trader Joe's".
   * Null when the link has no place segment (plain coordinate links).
   */
  parsePlaceName(url: string): string | null {
    const m = url.match(/\/maps\/place\/([^/@?#]+)/)
    if (!m) return null
    try {
      const name = decodeURIComponent(m[1].replace(/\+/g, ' ')).trim()
      return name || null
    } catch {
      return null
    }
  }

  /**
   * Bulk-create places from parsed Google Takeout data.
   * Server enforces dedup by google_place_id + 80m proximity; we just pass
   * everything the user confirmed. Chunked at the call site if large.
   */
  async batchImport(
    places: Array<{
      title: string
      address?: string | null
      latitude: number
      longitude: number
      google_place_id?: string | null
      tags?: string[]
      radius_m?: number
    }>,
  ): Promise<{
    created: Place[]
    skipped: Array<{
      index: number
      reason: 'google_place_id' | 'proximity' | 'invalid'
      existing_place_id?: number
    }>
  }> {
    const resp = await fetch(`${this.baseUrl}/import`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ places }),
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to import places')
    }
    return {
      created: (data.created ?? []) as Place[],
      skipped: (data.skipped ?? []) as Array<{
        index: number
        reason: 'google_place_id' | 'proximity' | 'invalid'
        existing_place_id?: number
      }>,
    }
  }
}

export const placeService = new PlaceService()
export default placeService


/**
 * The list page's one read: every place, with what is waiting at it.
 *
 * Separate from `list()` because the plain list has other callers (geofence
 * sync, the place picker) that do not want the task join. Falls back to the
 * plain list if the board route is unavailable, so an older server degrades to
 * "no counts" rather than an empty page.
 */
export async function placesBoard(): Promise<PlaceWithWaiting[]> {
  const res = await fetch('/api/places/board', { credentials: 'include' })
  if (!res.ok) return placeService.list()
  const body = await res.json().catch(() => null)
  if (!body?.success || !Array.isArray(body.data)) return placeService.list()
  return body.data as PlaceWithWaiting[]
}
